import { Story } from '../../types/story';

export const londonFootball3: Story = {
  id: 'london-football-3',
  title: 'The Locker Room - Part 3',
  excerpt: 'The morning after brings consequences as James faces his wife with the truth, the team discovers their secret, and three men must decide if their unconventional relationship is worth risking everything for.',
  city: 'London',
  publishDate: '2026-01-26',
  readTime: '42 min read',
  tags: ['♂♂♂', 'football', 'consequences', 'relationship', 'honesty'],
  categories: ['intense', 'queer'],
  intensity: 3,
  content: `Sunday morning came too soon.

I woke up in Marco's bed, tangled between him and Liam, the morning light filtering through the huge windows. For a moment, I forgot everything—forgot Sarah, forgot my marriage, forgot the complicated mess I'd made of my life. I just felt warm, safe, wanted.

Then my phone buzzed on the nightstand. A text from Sarah: "Just got back to Brighton station. Be home around 6. Miss you!"

The guilt hit me like a physical blow. Here I was, in bed with two men, while my wife was texting me that she missed me.

"I have to go," I said, trying to untangle myself without waking them.

"Stay," Liam murmured, his hand finding mine. "Just a little longer."

"I can't," I said. "Sarah's coming home tonight."

Marco's eyes opened. "Are you going to tell her?"

"I have to," I said. "I can't keep lying to her."

"What are you going to say?" Liam asked, sitting up.

"The truth," I said, though I had no idea how to even begin.

They both watched me get dressed, their expressions unreadable. The air was thick with unspoken fears, with the knowledge that everything was about to change.

"Call us," Marco said as I was leaving. "Whatever happens, call us."

"I will," I promised, but I wasn't sure I believed it.

My flat felt wrong when I got home. Too quiet, too neat, too much like a place that belonged to someone else. I cleaned obsessively, trying to erase the evidence of my night away, trying to prepare for the conversation I knew was coming.

Sarah arrived at 6:15, cheerful and sun-kissed from her day in Brighton.

"Miss me?" she asked, dropping her bags and wrapping her arms around me.

"Of course," I lied, and the lie felt like ash in my mouth.

We had dinner—Thai takeaway, our usual Sunday ritual. Sarah talked about her sister, about the shops they'd visited, about the gossip she'd caught up on. I barely listened, my mind racing with what I needed to say.

"James?" she said, noticing my distraction. "Are you okay? You seem a million miles away."

"I need to talk to you," I said, my voice shaking slightly.

"About what? Work stuff again?"

"No. About us."

She put down her fork, her expression concerned. "What about us?"

I took a deep breath. "I'm not happy, Sarah. I haven't been happy for a long time."

She looked confused, then hurt. "What are you talking about? We're fine."

"We're not fine," I said. "We haven't been fine for years. We're just... comfortable. We're just going through the motions."

"Where is this coming from?" she asked, her voice tight with emotion. "Did something happen at work? Did you meet someone?"

I hesitated. "Yes and no."

"What does that mean?"

"It means... I've been exploring things. About myself. About what I want."

"What things?" she pressed. "James, you're scaring me."

"I think I might be... not straight," I said, the words feeling foreign and terrifying.

She stared at me, her mouth slightly open. "What? Are you serious?"

"I think so," I said. "I've been... experimenting. With men."

The silence that followed was deafening. I could see the wheels turning in her head, the pieces clicking into place—the late nights, the distance, the lack of intimacy.

"Who?" she asked finally, her voice barely a whisper.

"People from the football team," I admitted. "Liam and Marco."

"Liam and Marco," she repeated, like she was testing the words. "Your friends."

"They're more than friends now," I said gently. "I'm sorry, Sarah. I never meant to hurt you."

"When did this start?" she asked, and I could hear the pain in her voice.

"A few weeks ago. But I've been curious for longer."

"So our whole marriage... has it been a lie?"

"No," I said quickly. "Not a lie. Just... incomplete. I didn't know. I swear I didn't know."

She stood up, started pacing the small living room. "So what happens now? You leave me for them? You just... what?"

"I don't know," I admitted. "I just know I can't keep lying to you. I can't keep living this half-life."

"Half-life?" she laughed, but it was a bitter, broken sound. "I thought we had a good life, James. I thought we were happy."

"We were comfortable," I corrected. "There's a difference."

"God," she said, sinking onto the sofa. "All this time... all those years... and you were just... what? Curious?"

"It's more than curiosity," I said. "It's real. What I feel for them... it's real."

She looked at me then, really looked at me, and I saw the moment she understood. The moment she realized this wasn't just a phase, wasn't just experimentation.

"You're leaving me," she said. It wasn't a question.

"I think I have to," I said gently. "For both of us. You deserve someone who can love you completely. And I deserve to be honest about who I am."

"How long do I have?" she asked, her voice surprisingly calm.

"I can stay until I find a place," I offered. "I don't want to just abandon you."

"No," she said. "I think it would be better if you went tonight. I can't... I can't look at you right now."

I nodded, understanding. "I'll pack a bag."

It didn't take long. I threw some clothes, my toiletries, my laptop into a duffel bag. When I came back to the living room, Sarah was sitting on the sofa, crying silently.

"I'm so sorry," I said, kneeling in front of her. "I never wanted to hurt you."

"I know," she whispered. "That's the worst part. I know you didn't."

I called Liam from a cab on the way to... nowhere. I didn't know where I was going.

"James? What's wrong?" he asked, his voice immediately concerned.

"I told her," I said. "I left."

"Where are you?" he asked. "Come to our place. Both of us. We have a spare room."

"You don't have to—"

"We want to," he insisted. "Where are you?"

I gave the cab driver Marco's address, and twenty minutes later, I was standing on their doorstep, duffel bag in hand, feeling like I'd lost everything and gained everything at the same time.

They took me in without questions, without judgment. Marco made me tea, Liam sat with me on the sofa, and for the first time all day, I felt like I could breathe.

"What happens now?" I asked, my voice rough.

"Now we figure it out," Marco said. "Together."

The next few weeks were a blur of change and adjustment. I found a small flat near theirs, started the process of separating my life from Sarah's. We met with lawyers, divided our assets, said goodbye to the life we'd built together.

Sarah was surprisingly civil about it all. We met for coffee a few weeks after I left, and she told me she'd started seeing someone—a colleague from work.

"I think I always knew," she said, stirring her coffee. "That there was something... missing. Between us."

"I'm sorry I didn't figure it out sooner," I said.

"Better late than never," she said, and there was no bitterness in her voice. Just resignation.

The team found out about a month after I left Sarah. We were at the pub after a match, and someone made a joke about me spending so much time with Liam and Marco.

"They're not just my friends," I said, before I could stop myself. "They're my boyfriends."

The silence that followed was uncomfortable, broken eventually by Dave, our team captain. "Good for you, mate. As long as you're happy."

And that was it. Nobody else seemed to care. Or if they did, they didn't say anything.

Our relationship settled into something comfortable, something real. The three of us fell into a routine—football on Saturdays, lazy Sundays together, weeknights spent cooking dinner or watching movies or just being together.

The sex was incredible—three bodies moving together, three mouths, three pairs of hands, three hearts beating as one. But it was the moments after that really mattered—lying tangled together, talking about everything and nothing, feeling completely safe and completely loved.

Six months after I left Sarah, we moved in together. A three-bedroom flat in Hackney, close to the football pitch, close to our lives. It felt like home in a way our marriage never had.

One night, lying in bed between them, I thought about how much my life had changed. How much I'd changed.

"You're thinking too hard," Liam murmured, his hand on my chest.

"Just remembering," I said. "How different everything is now."

"Better different?" Marco asked, his arm around my waist.

"So much better," I said. "I never knew it could be like this."

"Like what?" Liam pressed.

"Like this," I said. "Like... everything. Like being completely myself. Like being completely loved."

"Good," Marco said, kissing my forehead. "Because that's what we want for you. Always."

"Always," Liam agreed.

I fell asleep between them, feeling the steady rhythm of their breathing, feeling the warmth of their bodies, feeling completely and utterly at peace.

This wasn't the life I'd planned. It wasn't the life I'd expected. But it was the life I'd chosen, the life I'd fought for, the life that was finally, truly mine.

And as I drifted off to sleep, I knew that despite all the pain, all the complications, all the consequences...

It was worth it.

It was so worth it.`
};
